// Open/close, highlight, filtering and keyboard navigation for the shared
// Select's listbox, split out of components/Select.tsx (BL-2822) so the component stays the composition.

import { useCallback, useEffect, useMemo, useState, type RefObject } from 'react';

import type { SelectOption } from '../components/Select';
import { useOutsideClick } from './useOutsideClick';

/** Step the highlight to the next/previous selectable index, wrapping. */
function stepIndex(indices: number[], prev: number, dir: 1 | -1): number {
    if (indices.length === 0) return prev;
    const pos = indices.indexOf(prev);
    const nextPos = pos < 0 ? (dir === 1 ? 0 : indices.length - 1) : (pos + dir + indices.length) % indices.length;
    return indices[nextPos];
}

type KeyIntent =
    | { type: 'open' }
    | { type: 'select' }
    | { type: 'move'; dir: 1 | -1 }
    | { type: 'edge'; edge: 'first' | 'last' }
    | { type: 'close'; focus: boolean }
    | null;

/**
 * Resolve a keydown to a navigation intent. `fromSearch` lets Space type a
 * literal space in the filter field instead of selecting the highlight.
 */
function resolveKeyIntent(key: string, isOpen: boolean, fromSearch: boolean): KeyIntent {
    switch (key) {
        case 'Enter':
            return isOpen ? { type: 'select' } : { type: 'open' };
        case ' ':
            if (fromSearch && isOpen) return null; // let the space type
            return isOpen ? { type: 'select' } : { type: 'open' };
        case 'ArrowDown':
            return isOpen ? { type: 'move', dir: 1 } : { type: 'open' };
        case 'ArrowUp':
            return isOpen ? { type: 'move', dir: -1 } : { type: 'open' };
        case 'Home':
            return isOpen ? { type: 'edge', edge: 'first' } : null;
        case 'End':
            return isOpen ? { type: 'edge', edge: 'last' } : null;
        case 'Escape':
            return isOpen ? { type: 'close', focus: true } : null;
        case 'Tab':
            return isOpen ? { type: 'close', focus: false } : null;
        default:
            return null;
    }
}

/** Render order: special options pinned first, then the rest in place. */
function buildRenderOrder(options: SelectOption[], searchable: boolean, query: string): SelectOption[] {
    const q = query.trim().toLowerCase();
    const visible =
        searchable && q
            ? options.filter((o) => o.isSpecial || o.label.toLowerCase().includes(q))
            : options;
    return [...visible.filter((o) => o.isSpecial), ...visible.filter((o) => !o.isSpecial)];
}

interface ListboxState {
    isOpen: boolean;
    highlightedIndex: number;
    setHighlightedIndex: (i: number) => void;
    searchQuery: string;
    setSearchQuery: (q: string) => void;
    renderOrder: SelectOption[];
    activeDescendant: string | undefined;
    open: () => void;
    close: (focusButton?: boolean) => void;
    choose: (value: string) => void;
    handleKeyDown: (e: React.KeyboardEvent, fromSearch?: boolean) => void;
}

/**
 * Open/close state, highlight tracking, filtering, and keyboard navigation for
 * {@link Select}. The refs are owned by the component and passed in; the hook
 * only touches their `.current` inside effects and handlers.
 */
export function useSelectListbox(deps: {
    value: string;
    options: SelectOption[];
    onChange: (value: string) => void;
    disabled: boolean;
    searchable: boolean;
    listboxId: string;
    onOpen?: () => void;
    rootRef: RefObject<HTMLDivElement | null>;
    buttonRef: RefObject<HTMLButtonElement | null>;
    listRef: RefObject<HTMLUListElement | null>;
    popupRef: RefObject<HTMLDivElement | null>;
}): ListboxState {
    const { value, options, onChange, disabled, searchable, listboxId, onOpen, rootRef, buttonRef, listRef, popupRef } = deps;
    const [isOpen, setIsOpen] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const [searchQuery, setSearchQuery] = useState('');

    const renderOrder = useMemo(
        () => buildRenderOrder(options, searchable, searchQuery),
        [options, searchable, searchQuery],
    );
    const selectableIndices = useMemo(
        () => renderOrder.map((o, i) => (o.disabled || o.isGroupHeader ? -1 : i)).filter((i) => i >= 0),
        [renderOrder],
    );

    // Close on outside click. The popup is portaled to <body> (NOT inside
    // rootRef), so both rootRef AND popupRef count as "inside" — else a click
    // on an option would register as outside and close before its onClick fires.
    useOutsideClick(
        [rootRef, popupRef],
        () => {
            setIsOpen(false);
            setSearchQuery('');
        },
        { enabled: isOpen },
    );

    // Keep the highlighted row scrolled into view.
    useEffect(() => {
        if (!isOpen || highlightedIndex < 0) return;
        listRef.current?.querySelector(`[data-index="${highlightedIndex}"]`)?.scrollIntoView({ block: 'nearest' });
    }, [highlightedIndex, isOpen, listRef]);

    // Re-anchor the highlight to the first match whenever the filter query
    // changes. Deliberately keyed on `searchQuery` ALONE:
    //   - `selectableIndices` derives from `searchQuery` in the same render, so
    //     a query change re-runs this effect with the fresh value already.
    //   - Adding `selectableIndices` to the deps would re-anchor on every parent
    //     re-render that passes a fresh `options` array reference (common with
    //     inline/mapped options), yanking the highlight to the top mid-nav.
    //   - Relies on the invariant that `searchQuery` is reset to '' on every
    //     close path (see `close()`), so the dropdown never reopens with a stale
    //     query that this effect would need to react to.
    // (packages/ui's ESLint config has no react-hooks plugin, so there is no
    // exhaustive-deps directive to add or suppress here.)
    useEffect(() => {
        if (isOpen && searchQuery) setHighlightedIndex(selectableIndices[0] ?? -1);
    }, [searchQuery]);

    const open = useCallback(() => {
        if (disabled) return;
        const idx = renderOrder.findIndex((o) => o.value === value && !o.disabled && !o.isGroupHeader);
        setHighlightedIndex(idx >= 0 ? idx : (selectableIndices[0] ?? -1));
        setIsOpen(true);
        onOpen?.();
    }, [disabled, renderOrder, value, selectableIndices, onOpen]);

    const close = useCallback(
        (focusButton = true) => {
            setIsOpen(false);
            setHighlightedIndex(-1);
            setSearchQuery('');
            if (focusButton) buttonRef.current?.focus();
        },
        [buttonRef],
    );

    const choose = useCallback(
        (optionValue: string) => {
            onChange(optionValue);
            close();
        },
        [onChange, close],
    );

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent, fromSearch = false) => {
            if (disabled) return;
            const intent = resolveKeyIntent(e.key, isOpen, fromSearch);
            if (!intent) return;
            switch (intent.type) {
                case 'open':
                    e.preventDefault();
                    open();
                    break;
                case 'select':
                    e.preventDefault();
                    if (highlightedIndex >= 0 && !renderOrder[highlightedIndex]?.disabled) {
                        choose(renderOrder[highlightedIndex].value);
                    }
                    break;
                case 'move':
                    e.preventDefault();
                    setHighlightedIndex(stepIndex(selectableIndices, highlightedIndex, intent.dir));
                    break;
                case 'edge':
                    e.preventDefault();
                    setHighlightedIndex(
                        intent.edge === 'first'
                            ? (selectableIndices[0] ?? -1)
                            : (selectableIndices[selectableIndices.length - 1] ?? -1),
                    );
                    break;
                case 'close':
                    if (intent.focus) {
                        e.preventDefault();
                        e.stopPropagation();
                    }
                    close(intent.focus);
                    break;
            }
        },
        [disabled, isOpen, highlightedIndex, renderOrder, open, choose, close, selectableIndices],
    );

    const activeDescendant =
        isOpen && highlightedIndex >= 0 ? `${listboxId}-opt-${highlightedIndex}` : undefined;

    return {
        isOpen,
        highlightedIndex,
        setHighlightedIndex,
        searchQuery,
        setSearchQuery,
        renderOrder,
        activeDescendant,
        open,
        close,
        choose,
        handleKeyDown,
    };
}
